import {
  assertAttackDefinition, assertSpatialDefinition, getMvpCharacterDefinition, MVP_CHARACTER_DEFINITIONS, toSpatialDefinition,
} from '../../core';
import type { CharacterDefinition, StageBounds } from '../../core';
import { AppError } from '../errors/app-error';

export interface AssetMetadata { readonly id: string; readonly url: string; readonly required: boolean; readonly kind: 'TEXTURE' | 'FONT' | 'AUDIO' }
export interface StageDefinition { readonly id: string; readonly bounds: StageBounds }

const uniqueById = <T extends { readonly id: string }>(items: readonly T[], label: string): Map<string, T> => {
  const map = new Map<string, T>();
  for (const item of items) { if (map.has(item.id)) throw new AppError('CONTENT_ERROR', `Duplicate ${label} id: ${item.id}`); map.set(item.id, item); }
  return map;
};

export class ContentRegistry {
  private readonly characters: ReadonlyMap<string, CharacterDefinition>;
  private readonly stages: ReadonlyMap<string, StageDefinition>;
  private readonly assets: ReadonlyMap<string, AssetMetadata>;
  public constructor(characters: readonly CharacterDefinition[], stages: readonly StageDefinition[], assets: readonly AssetMetadata[]) {
    this.characters = uniqueById(characters, 'character'); this.stages = uniqueById(stages, 'stage'); this.assets = uniqueById(assets, 'asset');
    if (!this.stages.has('late-night-office')) throw new AppError('CONTENT_ERROR', 'Undefined required stage: late-night-office');
    for (const stage of stages) {
      const { minX, maxX, groundY } = stage.bounds;
      if (stage.id.length === 0 || !Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(groundY) || minX >= maxX) {
        throw new AppError('CONTENT_ERROR', `Invalid stage domain: ${stage.id}`);
      }
    }
    const attacks = uniqueById(characters.flatMap((character) => [...character.attacks]), 'attack');
    const traps = uniqueById(characters.flatMap((character) => [...character.trapDefinitions]), 'trap');
    for (const character of characters) {
      if (character.id.length === 0 || character.displayName.length === 0 || !Number.isFinite(character.maxHp) || character.maxHp <= 0
        || character.skills.length !== 2) throw new AppError('CONTENT_ERROR', `Invalid character metadata: ${character.id}`);
      try { assertSpatialDefinition(toSpatialDefinition(character)); } catch (error) { throw new AppError('CONTENT_ERROR', `Invalid character domain: ${character.id}`, true, { cause: error }); }
      for (const attack of character.attacks) {
        try { assertAttackDefinition(attack); } catch (error) { throw new AppError('CONTENT_ERROR', `Invalid attack: ${attack.id}`, true, { cause: error }); }
        for (const route of attack.cancelRoutes) if (!attacks.has(route.targetAttackId)) throw new AppError('CONTENT_ERROR', `Undefined targetAttackId: ${route.targetAttackId}`);
        for (const effect of attack.effects) {
          if (effect.responseAttackId !== undefined && !attacks.has(effect.responseAttackId)) throw new AppError('CONTENT_ERROR', `Undefined responseAttackId: ${effect.responseAttackId}`);
          if (effect.trapDefinitionId !== undefined && !traps.has(effect.trapDefinitionId)) throw new AppError('CONTENT_ERROR', `Undefined trapDefinitionId: ${effect.trapDefinitionId}`);
        }
      }
      for (const attackId of [...Object.values(character.attackBindings), ...Object.values(character.airborneAttackBindings)]) {
        if (attackId !== undefined && !attacks.has(attackId)) throw new AppError('CONTENT_ERROR', `Undefined attack reference: ${attackId}`);
      }
      for (const attackId of [...character.skills, character.super]) if (!attacks.has(attackId)) {
        throw new AppError('CONTENT_ERROR', `Undefined character attack reference: ${attackId}`);
      }
    }
    for (const trap of traps.values()) if (!attacks.has(trap.attackId)) throw new AppError('CONTENT_ERROR', `Undefined trap attack: ${trap.attackId}`);
    for (const asset of assets) if (asset.id.length === 0 || asset.url.length === 0 || typeof asset.required !== 'boolean'
      || !['TEXTURE', 'FONT', 'AUDIO'].includes(asset.kind)) {
      throw new AppError('CONTENT_ERROR', `Invalid asset metadata: ${asset.id}`);
    }
  }
  public getCharacter(id: string): CharacterDefinition {
    const value = this.characters.get(id); if (value === undefined) throw new AppError('CONTENT_ERROR', `Invalid character reference: ${id}`); return value;
  }
  public getStage(id: string): StageDefinition {
    const value = this.stages.get(id); if (value === undefined) throw new AppError('CONTENT_ERROR', `Undefined stage: ${id}`); return value;
  }
  public getAssets(): readonly AssetMetadata[] { return Object.freeze([...this.assets.values()].map((asset) => Object.freeze({ ...asset }))); }
}

export const createMvpContentRegistry = (assets: readonly AssetMetadata[]): ContentRegistry => {
  for (const definition of MVP_CHARACTER_DEFINITIONS) getMvpCharacterDefinition(definition.id);
  return new ContentRegistry(MVP_CHARACTER_DEFINITIONS, [{ id: 'late-night-office', bounds: { minX: 0, maxX: 1_280, groundY: 620 } }], assets);
};
